import { ApiError, claimsExpiresAt, parseJwtClaims, unwrap, userFromJwtClaims, userFromProfileSnapshot, type BackendLoginData, type CurrentUser, type LoginPayload, type LoginResult, type ProfileSnapshot } from '@/core';
import { http } from './http';

/**
 * Auth/Identity — 后端文档 §6.0-②：`POST /auth/login`、`POST /auth/logout`、`GET /auth/profile`（§6.2-7 缺口）。
 * 验证码已退役（后端 commit a3fe381 解除校验，项目负责人 seq=135 通报）。
 *
 * 本文件是**契约适配边界**：部署版实测 `data = {token, expireMinutes, account}`（不含身份与
 * 权限快照），与前端内部 `LoginResult{accessToken, expiresIn, sessionTimeoutMinutes, user}`
 * 不同名，统一在此归一，业务层零感知。
 */

/** §6.0-② 会话 30min；后端未回 expireMinutes 时按冻结口径兜底 */
const SESSION_TIMEOUT_MINUTES = 30;

export async function login(payload: LoginPayload): Promise<LoginResult> {
  // 后端 commit a3fe381 起不再校验验证码，请求体只发 account + password。rememberAccount 是纯前端偏好，不入请求体。
  const raw = await unwrap<BackendLoginData>(
    http.post<BackendLoginData>(
      '/auth/login',
      { account: payload.account, password: payload.password },
      { skipAuth: true, silent: true },
    ),
  );
  return toLoginResult(raw);
}

/** 部署版响应 → 内部 LoginResult */
export function toLoginResult(raw: BackendLoginData): LoginResult {
  if (!raw || typeof raw.token !== 'string' || !raw.token) {
    throw new ApiError('登录响应缺少令牌，请重新登录', 'AUTH_TOKEN_MISSING');
  }
  const minutes = Number(raw.expireMinutes) > 0 ? Number(raw.expireMinutes) : SESSION_TIMEOUT_MINUTES;
  const claims = parseJwtClaims(raw.token);
  const user = raw.user ?? (claims ? userFromJwtClaims(claims) : null);
  if (!user) {
    // §6.2-7 未就绪且令牌无可解析声明：无法建立权限快照，明确失败而非静默放行
    // ⛔ 用户可见文案不带接口路径与 §编号：形状缺失的细节只留在 `code`／`ApiError` 结构里排查
    throw new ApiError('登录失败，请稍后重试', 'AUTH_PROFILE_MISSING');
  }
  // expireMinutes 是名义值，真实失效由令牌 exp 决定：取更早者，避免计时未到期却已 401
  const nominal = minutes * 60;
  const claimExp = claimsExpiresAt(claims);
  const fromClaims = claimExp ? Math.max(60, Math.floor((claimExp - Date.now()) / 1000)) : nominal;
  return {
    accessToken: raw.token,
    refreshToken: undefined,
    expiresIn: Math.min(nominal, fromClaims),
    sessionTimeoutMinutes: minutes,
    // t108（F11 完整链）：登录响应第 5 键 `forcePwdChange` 归一到身份快照——快照随会话持久化，
    // 因此刷新页面后首登拦截仍然在（否则一次 reload 就能绕过强制改密）。
    user: { ...user, forcePwdChange: raw.forcePwdChange === true },
  };
}

export function logout(): Promise<void> {
  return unwrap<void>(http.post<void>('/auth/logout'));
}

/**
 * t108（F11 完整链）：**改密出口**。后端 `POST /api/auth/password/change`（「首登强制改密唯一放行写口」）：
 * 核验当前口令 + 强度校验 + 换哈希，成功后**清两族成员表的首登位**（bpo_agency_member 与
 * biz_delivery_agency_member）。⛔ 前端不做任何本地口令校验替代（强度口径以服务端为准，本地只做即时提示）。
 */
export function changePassword(oldPassword: string, newPassword: string): Promise<void> {
  return unwrap<void>(http.post<void>('/auth/password/change', { oldPassword, newPassword }));
}

/**
 * §3.5-② / §6.2-7：刷新页面后必须调本端点重建权限态，**不得**沿用 localStorage 陈旧快照
 * （权限可能已被 `role.permVersion` 变更）。后端把权限拆成菜单级 + 按钮级两份清单，
 * 由 `userFromProfileSnapshot` 归一为前端扁平 `permissions`。
 *
 * 兼容三种实际落地形状：① §6.2-7 `ProfileSnapshot`（联调真值）；② 内部 `CurrentUser`
 * （mock/桩直接给扁平快照）；③ 登录态 `{token,…}`（部分实现复用登录响应体）。
 */
export async function fetchProfile(): Promise<CurrentUser> {
  const raw = await unwrap<ProfileSnapshot | CurrentUser | BackendLoginData>(http.get<ProfileSnapshot>('/auth/profile'));

  const snap = raw as ProfileSnapshot;
  if (snap && typeof snap === 'object' && snap.account && Array.isArray(snap.buttonPermissions)) {
    return userFromProfileSnapshot(snap);
  }

  const flat = raw as CurrentUser;
  if (flat && typeof flat === 'object' && flat.account && Array.isArray(flat.permissions)) return flat;

  const login = raw as BackendLoginData;
  if (login && typeof login === 'object' && login.token) return toLoginResult(login).user;

  // ⛔ 同上：`PROFILE_SCHEMA_MISMATCH` 是排查用的码，不上屏
  throw new ApiError('登录状态校验未通过，请重新登录', 'PROFILE_SCHEMA_MISMATCH');
}
